import { useMemo } from 'react'
import useSWR from 'swr'
import useSWRMutation, { SWRMutationConfiguration } from 'swr/mutation'

import { toApiPage, withQueryParams } from '@/utils'

import {
  CreatePostSchema,
  Post,
  PostSearchParams,
  PostsParams,
  PostsResponse,
  UpdatePostSchema
} from './types'
import { API } from '@/api/endpoints'
import { FetchError, useSWRFetcher } from '@/lib/swr/fetcher'

const SEARCH_BRACKET_ARRAY_KEYS = ['services_included']
const DEFAULT_POSTS_ORDER_BY: PostsParams['order_by'] = '-created_at'

/**
 * GET All
 */
export const usePosts = (params: PostsParams = {}) => {
  const { enabled = true } = params
  const swrKey = enabled
    ? withQueryParams(API.POSTS, {
        page: params.page !== undefined ? toApiPage(params.page) : undefined,
        per_page: params.per_page,
        order_by: params.order_by || DEFAULT_POSTS_ORDER_BY,
        q: params.q,
        to_approve: params.to_approve === true ? 1 : undefined,
        post_type: params.post_type !== undefined ? params.post_type : undefined,
        city_id: params.city_id
      })
    : null

  const { data, error, isLoading, mutate } = useSWR<PostsResponse>(swrKey)

  return {
    collection: data?.data,
    pagination: data
      ? {
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total
        }
      : undefined,
    isLoading,
    error,
    mutate
  }
}

export const getPostsSearchKey = (params?: PostSearchParams) => {
  if (!params) {
    return null
  }

  if (Object.keys(params).length === 0) {
    return API.POSTS_SEARCH
  }

  return withQueryParams(API.POSTS_SEARCH, params, {
    bracketArrayKeys: SEARCH_BRACKET_ARRAY_KEYS
  })
}

/**
 * Search | GET All
 */
export const usePostsSearch = (params?: PostSearchParams) => {
  const paramsKey = JSON.stringify(params)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const swrKey = useMemo(() => getPostsSearchKey(params), [paramsKey])

  const { data, error, isLoading, mutate } = useSWR<PostsResponse>(swrKey)

  return {
    collection: data?.data,
    pagination: data
      ? {
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total
        }
      : undefined,
    isLoading,
    error,
    mutate
  }
}

/**
 * GET
 */
export const usePost = (id: number | null) => {
  const { data, error, isLoading, mutate } = useSWR<Post>(id ? `${API.POSTS}/${id}` : null)

  return {
    post: data,
    isLoading,
    error,
    mutate
  }
}

/**
 * CREATE
 */
type CreatePostOptions = SWRMutationConfiguration<
  Post,
  FetchError,
  string,
  Partial<CreatePostSchema>,
  Post
> & {
  throwOnError?: boolean
}

export const useCreatePost = (options?: CreatePostOptions) => {
  const fetcher = useSWRFetcher()
  const { trigger, isMutating: isCreating } = useSWRMutation(
    API.POSTS,
    (url: string, { arg }: { arg: Partial<CreatePostSchema> }) =>
      fetcher<Post>(url, { method: 'POST', body: JSON.stringify(arg) }),
    options
  )

  return {
    createPost: trigger,
    isCreating
  }
}

/**
 * UPDATE
 */
type UpdatePostOptions = SWRMutationConfiguration<
  Post,
  FetchError,
  string,
  { id: number; data: Partial<UpdatePostSchema> },
  Post
> & {
  throwOnError?: boolean
}

export const useUpdatePost = (options?: UpdatePostOptions) => {
  const fetcher = useSWRFetcher()
  const { trigger, isMutating: isUpdating } = useSWRMutation(
    API.POSTS,
    (url: string, { arg }: { arg: { id: number; data: Partial<UpdatePostSchema> } }) =>
      fetcher<Post>(`${url}/${arg.id}`, { method: 'PUT', body: JSON.stringify(arg.data) }),
    options
  )

  return {
    updatePost: trigger,
    isUpdating
  }
}

/**
 * DELETE
 */
type DeletePostOptions = SWRMutationConfiguration<void, FetchError, string, number, void> & {
  throwOnError?: boolean
}

export const useDeletePost = (options?: DeletePostOptions) => {
  const fetcher = useSWRFetcher()
  const { trigger, isMutating: isDeleting } = useSWRMutation(
    API.POSTS,
    (url: string, { arg }: { arg: number }) => fetcher<void>(`${url}/${arg}`, { method: 'DELETE' }),
    options
  )

  return {
    deletePost: trigger,
    isDeleting
  }
}

/**
 * TOGGLE ACTIVE
 */
type ToggleActivePostOptions = SWRMutationConfiguration<
  { message: string },
  FetchError,
  string,
  { id: number; active: boolean },
  { message: string }
> & {
  throwOnError?: boolean
}

export const useToggleActivePost = (options?: ToggleActivePostOptions) => {
  const fetcher = useSWRFetcher()
  const { trigger, isMutating: isTogglingActive } = useSWRMutation(
    API.POSTS,
    (url: string, { arg }: { arg: { id: number; active: boolean } }) =>
      fetcher<{ message: string }>(`${url}/${arg.id}/toggle_active`, {
        method: 'POST',
        body: JSON.stringify({ active: arg.active })
      }),
    options
  )

  return {
    toggleActivePost: trigger,
    isTogglingActive
  }
}

/**
 * TOGGLE APPROVE
 */
type ToggleApprovePostOptions = SWRMutationConfiguration<
  { message: string },
  FetchError,
  string,
  { id: number; approved: boolean },
  { message: string }
> & {
  throwOnError?: boolean
}

export const useToggleApprovePost = (options?: ToggleApprovePostOptions) => {
  const fetcher = useSWRFetcher()
  const { trigger, isMutating: isTogglingApprove } = useSWRMutation(
    API.POSTS,
    (url: string, { arg }: { arg: { id: number; approved: boolean } }) =>
      fetcher<{ message: string }>(`${url}/${arg.id}/toggle_approve`, {
        method: 'POST',
        body: JSON.stringify({ approved: arg.approved })
      }),
    options
  )

  return {
    toggleApprovePost: trigger,
    isTogglingApprove
  }
}
